/**
 * Ball velocity-change trace — the deterministic instrumentation contract. (§3.2)
 *
 * The brief requires that EVERY material change to the ball's velocity be
 * attributable to exactly one physical cause, and that any change with NO
 * registered cause be detectable as a failing invariant:
 *
 *   "Every meaningful ball velocity change must identify one of these causes ...
 *    Any substantial velocity change without a cause is a failing invariant."
 *
 * ── This is a DIAGNOSTIC SIDE-CHANNEL, never authoritative state ─────────────
 *
 * The trace is NOT part of WorldState. It is not cloned, hashed, serialized or
 * rolled back, and — critically — it never feeds back into the simulation. Every
 * trace function only READS the ball's velocity; it never writes it. The sim
 * therefore produces bit-identical results whether or not a trace is attached
 * (asserted by ball-trace.test.ts), so determinism (§13.1) is preserved.
 *
 * ── Zero cost when off ───────────────────────────────────────────────────────
 *
 * With no sink attached (production default) every entry point is a single
 * null check and returns immediately. A sink can also sample (record only every
 * Nth tick) to bound overhead in long soak runs.
 *
 * ── How attribution works ────────────────────────────────────────────────────
 *
 * Each velocity-mutating site tags its delta with a BallVelocityCause (and the
 * responsible PlayerId, when there is one). Across a full step() the sink also
 * keeps a running sum of every tagged delta and compares it to the ball's NET
 * velocity change for the tick. Any residual beyond a float-noise threshold is a
 * change no system claimed — it is recorded as `Unexplained` and counted. That
 * residual is the invariant the brief asks for: healthy play leaves it at zero.
 *
 * Deliberately UNTAGGED: the ball.ts containment failsafe clamps (the world-edge
 * position clamp and the height ceiling). They are supposed to be dead code; if
 * one ever fires it SHOULD surface as `Unexplained`, which is exactly the signal
 * we want while those clamps still exist (removed/ widened in later phases).
 */

import type { WorldState } from './types.js';

/**
 * The cause of a ball velocity change. The first eleven are the brief's §3.2
 * cause set (POST_OR_CROSSBAR is split into Post/Crossbar for finer diagnostics;
 * Net folds into PostContact as a goal-frame surface). `Unexplained` is the
 * detection sentinel — a residual change no system tagged, i.e. a failing
 * invariant — not a physical cause.
 */
export const enum BallVelocityCause {
  Gravity = 0,
  AerodynamicDrag = 1,
  Magnus = 2,
  GroundContact = 3,
  PostContact = 4,
  CrossbarContact = 5,
  PlayerBodyContact = 6,
  FootTouch = 7,
  Kick = 8,
  GoalkeeperContact = 9,
  RuleRestart = 10,
  Unexplained = 11,
}

export const BALL_VELOCITY_CAUSE_NAMES: readonly string[] = [
  'Gravity',
  'AerodynamicDrag',
  'Magnus',
  'GroundContact',
  'PostContact',
  'CrossbarContact',
  'PlayerBodyContact',
  'FootTouch',
  'Kick',
  'GoalkeeperContact',
  'RuleRestart',
  'Unexplained',
];

/** One attributed change to the ball's velocity, stamped with the sim tick. */
export interface BallVelocityRecord {
  /** Deterministic sim tick (never wall-clock). */
  tick: number;
  cause: BallVelocityCause;
  /** PlayerId responsible, or -1 (aero, gravity, restart). */
  entity: number;
  /** The velocity delta attributed to this cause (m/s). */
  dvx: number;
  dvy: number;
  dvz: number;
  /** Ball velocity AFTER the change (m/s). */
  vx: number;
  vy: number;
  vz: number;
}

/**
 * A change smaller than this magnitude (m/s) is not worth a record — but it is
 * still summed into the per-tick accounting so the residual stays exact.
 */
const MATERIAL_DV = 1e-4;
const MATERIAL_DV_SQ = MATERIAL_DV * MATERIAL_DV;

/**
 * The per-tick residual (net change minus the sum of every tagged delta) must
 * exceed this to be reported as Unexplained. All the sim's ball math is f64 in a
 * single engine, so a fully-tagged tick leaves a residual at the 1e-12 level;
 * 1e-4 m/s is comfortably above that float noise and well below any real impulse
 * (gravity alone is ~0.16 m/s per tick).
 */
const UNEXPLAINED_DV = 1e-4;
const UNEXPLAINED_DV_SQ = UNEXPLAINED_DV * UNEXPLAINED_DV;

/** Hard cap on retained records so a long soak cannot grow unbounded. */
const DEFAULT_CAPACITY = 500_000;

/**
 * A trace sink. Attach one with setBallTrace() to start recording; detach with
 * setBallTrace(null). Purely diagnostic — see the file header.
 */
export class BallTrace {
  readonly records: BallVelocityRecord[] = [];
  enabled = true;
  /** Record only ticks where (tick % sampleEvery === 0). 1 = every tick. */
  sampleEvery: number;
  /** Records dropped because `capacity` was reached. */
  dropped = 0;
  /** Count of Unexplained residuals seen (failing-invariant tally). */
  unexplainedCount = 0;
  private readonly capacity: number;

  // Per-tick residual accounting (set by beginTick / endTick).
  private startVx = 0;
  private startVy = 0;
  private startVz = 0;
  private sumDvx = 0;
  private sumDvy = 0;
  private sumDvz = 0;
  private tracking = false;

  constructor(opts: { sampleEvery?: number; capacity?: number } = {}) {
    this.sampleEvery = opts.sampleEvery && opts.sampleEvery > 1 ? Math.floor(opts.sampleEvery) : 1;
    this.capacity = opts.capacity ?? DEFAULT_CAPACITY;
  }

  /** True when this tick is being sampled (cheap gate used by the free functions). */
  samplingTick(tick: number): boolean {
    return this.enabled && (this.sampleEvery === 1 || tick % this.sampleEvery === 0);
  }

  addDelta(tick: number, cause: BallVelocityCause, entity: number, dvx: number, dvy: number, dvz: number, vx: number, vy: number, vz: number): void {
    this.sumDvx += dvx;
    this.sumDvy += dvy;
    this.sumDvz += dvz;
    if (dvx * dvx + dvy * dvy + dvz * dvz < MATERIAL_DV_SQ) return;
    if (this.records.length >= this.capacity) {
      this.dropped++;
      return;
    }
    this.records.push({ tick, cause, entity, dvx, dvy, dvz, vx, vy, vz });
  }

  beginTick(vx: number, vy: number, vz: number): void {
    this.startVx = vx;
    this.startVy = vy;
    this.startVz = vz;
    this.sumDvx = 0;
    this.sumDvy = 0;
    this.sumDvz = 0;
    this.tracking = true;
  }

  endTick(tick: number, vx: number, vy: number, vz: number): void {
    if (!this.tracking) return;
    this.tracking = false;
    const rx = vx - this.startVx - this.sumDvx;
    const ry = vy - this.startVy - this.sumDvy;
    const rz = vz - this.startVz - this.sumDvz;
    if (rx * rx + ry * ry + rz * rz <= UNEXPLAINED_DV_SQ) return;
    this.unexplainedCount++;
    if (this.records.length >= this.capacity) {
      this.dropped++;
      return;
    }
    this.records.push({ tick, cause: BallVelocityCause.Unexplained, entity: -1, dvx: rx, dvy: ry, dvz: rz, vx, vy, vz });
  }

  clear(): void {
    this.records.length = 0;
    this.dropped = 0;
    this.unexplainedCount = 0;
    this.tracking = false;
  }

  /** Records for one cause. */
  byCause(cause: BallVelocityCause): BallVelocityRecord[] {
    return this.records.filter((r) => r.cause === cause);
  }

  /** Count per cause, indexed by the BallVelocityCause value. */
  countsByCause(): number[] {
    const counts = new Array(BALL_VELOCITY_CAUSE_NAMES.length).fill(0);
    for (const r of this.records) counts[r.cause]++;
    return counts;
  }
}

// ── Module-level active sink ────────────────────────────────────────────────
// Diagnostic only. Single-threaded JS means one sink at a time is sufficient for
// tests and dev harnesses; it never influences sim math (see header).

let active: BallTrace | null = null;

export function setBallTrace(sink: BallTrace | null): void {
  active = sink;
}

export function getBallTrace(): BallTrace | null {
  return active;
}

/** Cheap guard for call sites that must compute component deltas only when tracing. */
export function ballTraceOn(): boolean {
  return active !== null && active.enabled;
}

/**
 * Record a velocity delta given explicitly (used where the components are known
 * without a before/after snapshot, e.g. the split gravity/drag/Magnus deltas).
 */
export function traceBallDv(
  state: WorldState,
  cause: BallVelocityCause,
  entity: number,
  dvx: number,
  dvy: number,
  dvz: number,
): void {
  const t = active;
  if (t === null || !t.samplingTick(state.tick)) return;
  const b = state.ball;
  t.addDelta(state.tick, cause, entity, dvx, dvy, dvz, b.vx, b.vy, b.vz);
}

/**
 * Record whatever the ball's velocity changed by since the (vx0,vy0,vz0)
 * snapshot the caller took immediately before the mutating region.
 */
export function traceBallSince(
  state: WorldState,
  cause: BallVelocityCause,
  entity: number,
  vx0: number,
  vy0: number,
  vz0: number,
): void {
  const t = active;
  if (t === null || !t.samplingTick(state.tick)) return;
  const b = state.ball;
  t.addDelta(state.tick, cause, entity, b.vx - vx0, b.vy - vy0, b.vz - vz0, b.vx, b.vy, b.vz);
}

/** Snapshot the ball velocity at the start of a tick (called from step()). */
export function beginBallTraceTick(state: WorldState): void {
  const t = active;
  if (t === null || !t.samplingTick(state.tick)) return;
  const b = state.ball;
  t.beginTick(b.vx, b.vy, b.vz);
}

/** Close the tick: any net change no cause claimed becomes an Unexplained record. */
export function endBallTraceTick(state: WorldState): void {
  const t = active;
  if (t === null || !t.samplingTick(state.tick)) return;
  const b = state.ball;
  t.endTick(state.tick, b.vx, b.vy, b.vz);
}
